<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import Sortable from "sortablejs";
import { getTagDragZone, useTagsViewStore, type TagView } from "@shared/store/modules/tagsView";
import { usePermissionStore } from "@shared/store/modules/permission";
import { isRouteNoPin } from "@shared/router/routePageExtras";
import { t } from "@shared/utils/i18n";
import { getAppHomePath } from "@shared/utils/appHome";
import { isNavigableAppPath, resolveTagsViewNavigationTarget } from "@shared/utils/tagNavigation";
import ScrollPane from "./ScrollPane.vue";

const route = useRoute();
const router = useRouter();
const tagsStore = useTagsViewStore();
const permissionStore = usePermissionStore();

const visitedViews = computed(() => tagsStore.visitedViews);

const scrollPaneRef = ref<InstanceType<typeof ScrollPane>>();
const fixedListRef = ref<HTMLElement | null>(null);
const normalListRef = ref<HTMLElement | null>(null);
const tagRefs = ref<Record<string, HTMLElement | null>>({});
/** 拖拽结束强制重挂分区，清掉 Vue 未追踪的 fallback 残留节点 */
const listRenderKey = ref(0);
let fixedSortable: Sortable | null = null;
let normalSortable: Sortable | null = null;
/** 当前拖拽所在分区，用于限制 fallback 横向不出区 */
let dragZoneEl: HTMLElement | null = null;
/** 谷歌标签页效果：拖拽时只允许水平跟手，纵向锁定在原始行高上，不随鼠标上下漂移 */
let lockedFallbackTop: number | null = null;
/**
 * 拖拽中锁定列表 active：高亮被拖项，旧选中立即取消。
 * 路由在 onStart 立即切换；松手后清空锁定，与路由对齐。
 */
const dragActiveLockPath = ref<string | null>(null);
/** 拖拽结束后抑制一次 click，避免拖完误跳转 */
let suppressClick = false;

const isActive = (tag: TagView) => {
    if (dragActiveLockPath.value != null) {
        return tag.path === dragActiveLockPath.value;
    }
    if (tag.path === route.path) return true;
    const singletonPath = route.meta?.singletonTagPath as string | undefined;
    return Boolean(singletonPath && tag.path === singletonPath);
};
const isFixed = (tag: TagView) => tagsStore.isFixed(tag);
const tagZone = (tag: TagView) => getTagDragZone(tag, isFixed(tag));

const fixedZoneTags = computed(() => visitedViews.value.filter(tag => tagZone(tag) === "fixed"));
const normalZoneTags = computed(() => visitedViews.value.filter(tag => tagZone(tag) === "normal"));

function resolveTagRefKey(): string {
    return (route.meta?.singletonTagPath as string) || route.path;
}

function setElTransform(target: HTMLElement, value: string) {
    target.style.webkitTransform = value;
    target.style.setProperty("moz-transform", value);
    target.style.setProperty("ms-transform", value);
    target.style.transform = value;
}

function parseMatrixTransform(transform: string): number[] | null {
    if (transform.startsWith("matrix(")) {
        const parts = transform
            .slice(7, -1)
            .split(",")
            .map(s => parseFloat(s.trim()));
        return parts.length === 6 ? parts : null;
    }
    if (transform.startsWith("matrix3d(")) {
        const parts = transform
            .slice(9, -1)
            .split(",")
            .map(s => parseFloat(s.trim()));
        return parts.length === 16 ? parts : null;
    }
    return null;
}

/**
 * 谷歌标签页拖拽效果：
 * - 纵向完全锁定在原始行高上（不管鼠标怎么上下动，标签只沿着标签条这一条线水平滑动）
 * - 横向仍夹在当前分区矩形内，不允许滑出固定区 / 非固定区
 */
function clampFallbackToZone() {
    const fallback = document.querySelector(".tags-view-item--fallback") as HTMLElement | null;
    if (!fallback || !dragZoneEl) return;
    const transform = fallback.style.transform || fallback.style.webkitTransform || "";
    if (!transform || transform === "none") return;

    const parts = parseMatrixTransform(transform);
    if (!parts) return;

    const zoneRect = dragZoneEl.getBoundingClientRect();
    const fbRect = fallback.getBoundingClientRect();
    let dx = 0;
    if (fbRect.left < zoneRect.left) dx = zoneRect.left - fbRect.left;
    else if (fbRect.right > zoneRect.right) dx = zoneRect.right - fbRect.right;

    // 纵向：始终纠正回锁定的初始行高，而不是只在越界时才纠正
    const dy = lockedFallbackTop != null ? lockedFallbackTop - fbRect.top : 0;
    if (!dx && !dy) return;

    const is3d = parts.length === 16;
    if (is3d) {
        parts[12] += dx;
        parts[13] += dy;
    } else {
        parts[4] += dx;
        parts[5] += dy;
    }
    setElTransform(fallback, is3d ? `matrix3d(${parts.join(",")})` : `matrix(${parts.join(",")})`);
}

function bindFallbackZoneClamp() {
    document.addEventListener("pointermove", clampFallbackToZone, false);
    document.addEventListener("mousemove", clampFallbackToZone, false);
    document.addEventListener("touchmove", clampFallbackToZone, false);
}

function unbindFallbackZoneClamp() {
    document.removeEventListener("pointermove", clampFallbackToZone, false);
    document.removeEventListener("mousemove", clampFallbackToZone, false);
    document.removeEventListener("touchmove", clampFallbackToZone, false);
}

function collectTagPaths(): string[] {
    const paths: string[] = [];
    for (const list of [fixedListRef.value, normalListRef.value]) {
        if (!list) continue;
        for (const node of list.querySelectorAll<HTMLElement>(".tags-view-item")) {
            // 跳过 forceFallback 克隆，避免顺序/残留节点干扰
            if (node.classList.contains("tags-view-item--fallback")) continue;
            const path = node.dataset.path || "";
            if (path) paths.push(path);
        }
    }
    return paths;
}

/** 清掉 Sortable animation / drag 残留的 inline transform */
function clearDragInlineStyles(container?: HTMLElement | null) {
    const lists = container ? [container] : [fixedListRef.value, normalListRef.value];
    for (const list of lists) {
        if (!list) continue;
        for (const node of list.querySelectorAll<HTMLElement>(".tags-view-item")) {
            if (node.classList.contains("tags-view-item--fallback")) continue;
            node.style.transform = "";
            node.style.webkitTransform = "";
            node.style.transition = "";
            node.style.zIndex = "";
            node.style.willChange = "";
            node.style.opacity = "";
            node.style.visibility = "";
            node.style.pointerEvents = "";
            node.style.position = "";
            node.style.left = "";
            node.style.top = "";
            node.style.width = "";
            node.style.height = "";
            node.classList.remove("tags-view-item--ghost", "tags-view-item--chosen", "tags-view-item--drag");
        }
    }
}

/** Vue 不管理 fallback 克隆；拖拽中若发生补丁，克隆可能留在列表里/body 上造成重叠或残留 */
function removeFallbackClones() {
    document.querySelectorAll(".tags-view-item--fallback").forEach(node => node.remove());
}

function destroySortable() {
    unbindFallbackZoneClamp();
    dragZoneEl = null;
    lockedFallbackTop = null;
    dragActiveLockPath.value = null;
    fixedSortable?.destroy();
    normalSortable?.destroy();
    fixedSortable = null;
    normalSortable = null;
}

/**
 * 兜底：正常拖拽由 Sortable 的 onEnd 清理 fallback 克隆与拖拽状态；
 * 但松手位置在可拖拽区域外、拖拽中切窗口/失焦等边界场景下 onEnd 可能不触发，
 * 会导致 fallbackOnBody 生成的克隆永久挂在 body 上，看起来像多出来一个标签。
 * 这里在全局 mouseup/touchend 后延迟检查一次：若此时并非正常拖拽中却仍有残留克隆，强制清理。
 */
function forceCleanupStrayFallback() {
    if (dragZoneEl) return; // 正常拖拽中，交给 onEnd 处理
    if (!document.querySelector(".tags-view-item--fallback")) return;
    removeFallbackClones();
    clearDragInlineStyles();
    dragActiveLockPath.value = null;
}

function scheduleStrayFallbackCleanup() {
    window.setTimeout(forceCleanupStrayFallback, 80);
}

function createZoneSortable(el: HTMLElement, options?: { lockAffix?: boolean }) {
    return Sortable.create(el, {
        // 谷歌标签页效果：其余标签让位时有一点平滑滑动动画，而不是瞬间跳位
        animation: 150,
        easing: "cubic-bezier(0.2, 0, 0, 1)",
        direction: "horizontal",
        draggable: ".tags-view-item",
        filter: ".tags-view-item--affix",
        preventOnFilter: false,
        // 分区互不互通
        group: {
            name: `tags-view-${el.dataset.zone || "zone"}`,
            pull: false,
            put: false,
        },
        forceFallback: true,
        // helper 挂 body，避免落在 Vue v-for 容器内变成残留重叠节点
        fallbackOnBody: true,
        fallbackTolerance: 0,
        fallbackClass: "tags-view-item--fallback",
        ghostClass: "tags-view-item--ghost",
        chosenClass: "tags-view-item--chosen",
        dragClass: "tags-view-item--drag",
        onStart(evt) {
            dragZoneEl = el;
            const dragEl = evt.item as HTMLElement;
            const path = dragEl.dataset?.path || "";
            // 谷歌标签页效果：记录拿起瞬间的行高位置，拖拽全程纵向锁死在这条线上
            lockedFallbackTop = dragEl.getBoundingClientRect().top;

            // 直接用 inline style 隐藏原始节点，不依赖 ghostClass：
            // 下面 dragActiveLockPath 变化会触发 Vue 重新计算该 tag 的 :class（此时 isActive 变 true），
            // Vue 的 class patch 会整体重写 className，把 Sortable 刚加上的 ghostClass 冲掉，
            // 导致原始节点重新可见、和跟手的 fallback helper 一起移动，看起来像"两个一样的标签"。
            // style 属性不受 :class 绑定影响，用它兜底隐藏才不会被 Vue 覆盖。
            dragEl.style.visibility = "hidden";
            dragEl.style.opacity = "0";
            dragEl.style.pointerEvents = "none";

            // 锁定高亮到被拖项：旧选中立刻取消，跟手 helper 也是选中态；同时立刻改路由
            dragActiveLockPath.value = path || null;
            bindFallbackZoneClamp();
            clampFallbackToZone();
            if (path) {
                const tag = tagsStore.visitedViews.find(v => v.path === path);
                if (tag) {
                    if (tag.affix) router.push(getAppHomePath());
                    else router.push(tag.fullPath || tag.path);
                }
            }
        },
        onMove(evt) {
            const related = evt.related;
            if (!related) return false;
            // 系统首页不可被挤到前面
            if (options?.lockAffix && related.dataset.affix === "1" && !evt.willInsertAfter) return false;
            return true;
        },
        onEnd() {
            unbindFallbackZoneClamp();
            dragZoneEl = null;
            lockedFallbackTop = null;
            suppressClick = true;
            window.setTimeout(() => {
                suppressClick = false;
            }, 0);

            removeFallbackClones();
            clearDragInlineStyles(el);
            tagsStore.reorderVisitedByPaths(collectTagPaths());

            listRenderKey.value += 1;
            nextTick(() => {
                removeFallbackClones();
                clearDragInlineStyles();
                initSortable();
                // 解锁后列表 active 同步到已切换的路由
                dragActiveLockPath.value = null;
            });
        },
    });
}

function initSortable() {
    destroySortable();
    removeFallbackClones();
    const fixedEl = fixedListRef.value;
    const normalEl = normalListRef.value;
    if (fixedEl) fixedSortable = createZoneSortable(fixedEl, { lockAffix: true });
    if (normalEl) normalSortable = createZoneSortable(normalEl);
}

// ----------------------------------------------------------------
// 路由变化时，确保当前 tag 滚动到可视区
// ----------------------------------------------------------------
watch(
    () => route.fullPath,
    () => {
        // 拖拽中不滚动标签条，避免干扰 Sortable
        if (dragActiveLockPath.value != null) return;
        nextTick(() => {
            const target = tagRefs.value[resolveTagRefKey()];
            if (target && scrollPaneRef.value) {
                scrollPaneRef.value.moveToTarget(target);
            }
        });
    },
);

async function bootstrapTags() {
    if (!permissionStore.loaded) return;
    if (!tagsStore.hydrated) {
        await tagsStore.hydrateFromPreference();
    }
    tagsStore.initAffixViews(permissionStore.pageRoutes);
    tagsStore.pruneInaccessibleViews(router);
    if (route.path !== "/404" && route.path !== "/403" && !isNavigableAppPath(route.path, router)) {
        tagsStore.dropPath((route.meta?.singletonTagPath as string) || route.path);
        return;
    }
    tagsStore.addView(route);
}

// 动态路由就绪后：从通用偏好拉取全部页签 / 固定状态，再注入 affix
watch(
    () => permissionStore.loaded,
    loaded => {
        if (!loaded) return;
        void bootstrapTags();
    },
    { immediate: true },
);

onMounted(() => {
    void bootstrapTags();
    nextTick(() => initSortable());
    document.addEventListener("mouseup", scheduleStrayFallbackCleanup);
    document.addEventListener("touchend", scheduleStrayFallbackCleanup);
    document.addEventListener("touchcancel", scheduleStrayFallbackCleanup);
});

onBeforeUnmount(() => {
    document.removeEventListener("mouseup", scheduleStrayFallbackCleanup);
    document.removeEventListener("touchend", scheduleStrayFallbackCleanup);
    document.removeEventListener("touchcancel", scheduleStrayFallbackCleanup);
    destroySortable();
    removeFallbackClones();
});

// 页签增删 / 固定状态变化时重建；纯排序不重建（避免拖拽结束后抖动）
watch(
    () =>
        visitedViews.value
            .map(v => `${v.path}:${tagZone(v)}`)
            .sort()
            .join("|"),
    () => {
        nextTick(() => initSortable());
    },
);

function setTagRef(path: string, el: any) {
    tagRefs.value[path] = el;
}

function pushTagNavigation(preferFromIndex?: number) {
    router.push(
        resolveTagsViewNavigationTarget({
            preferFromIndex,
            visitedViews: tagsStore.visitedViews,
            router,
            sidebarMenus: permissionStore.sidebarMenus,
        }),
    );
}

function navigateTo(tag: TagView) {
    if (suppressClick) return;
    if (tag.affix) {
        router.push(getAppHomePath());
        return;
    }
    if (isNavigableAppPath(tag.path, router)) {
        router.push(tag.fullPath || tag.path);
        return;
    }
    tagsStore.dropPath(tag.path);
    const idx = tagsStore.visitedViews.findIndex(v => v.path === tag.path);
    pushTagNavigation(idx >= 0 ? idx - 1 : undefined);
}

// ----------------------------------------------------------------
// 关闭逻辑
// ----------------------------------------------------------------
function closeTag(tag: TagView, e?: MouseEvent) {
    e?.stopPropagation();
    if (tag.affix || isFixed(tag)) return;

    const idx = tagsStore.visitedViews.findIndex(v => v.path === tag.path);
    const wasActive = isActive(tag);
    tagsStore.delView(tag);
    if (wasActive) {
        pushTagNavigation(idx > 0 ? idx - 1 : undefined);
    }
}

function closeOthers() {
    if (!contextTag.value) return;
    const keep = contextTag.value;
    tagsStore.delOthersViews(keep);
    if (!isActive(keep)) {
        const idx = tagsStore.visitedViews.findIndex(v => v.path === keep.path);
        pushTagNavigation(idx >= 0 ? idx : undefined);
    }
}

function closeAll() {
    tagsStore.delAllViews();
    if (!visitedViews.value.find(v => isActive(v))) {
        pushTagNavigation();
    }
}

function refreshTag(tag: TagView) {
    router.replace("/redirect" + tag.fullPath);
}

function fixTag(tag: TagView) {
    tagsStore.addFixed(tag);
}

function unfixTag(tag: TagView) {
    tagsStore.removeFixed(tag);
}

function canPinTag(tag: TagView): boolean {
    return !tag.affix && !isRouteNoPin(tag.name);
}

// ----------------------------------------------------------------
// 右键菜单
// ----------------------------------------------------------------
const contextVisible = ref(false);
const contextLeft = ref(0);
const contextTop = ref(0);
const contextTag = ref<TagView | null>(null);

function openContextMenu(tag: TagView, e: MouseEvent) {
    e.preventDefault();
    contextTag.value = tag;

    const offsetWidth = 160;
    const offsetTop = 8;
    const maxLeft = window.innerWidth - offsetWidth;
    contextLeft.value = Math.min(e.clientX, maxLeft);
    contextTop.value = e.clientY + offsetTop;
    contextVisible.value = true;
}

function closeContextMenu() {
    contextVisible.value = false;
}

watch(contextVisible, val => {
    if (val) {
        document.body.addEventListener("click", closeContextMenu);
    } else {
        document.body.removeEventListener("click", closeContextMenu);
    }
});
</script>

<template>
    <div class="tags-view-container">
        <ScrollPane ref="scrollPaneRef" class="scroll-pane">
            <div class="tags-view-list">
                <!-- 固定区：首页 + 用户固定，仅区内排序 -->
                <div v-show="fixedZoneTags.length" :key="`fixed-${listRenderKey}`" ref="fixedListRef" class="tags-view-list__zone" data-zone="fixed">
                    <div
                        v-for="tag in fixedZoneTags"
                        :key="tag.path"
                        :ref="el => setTagRef(tag.path, el as HTMLElement | null)"
                        class="tags-view-item"
                        :class="{ active: isActive(tag), 'tags-view-item--affix': !!tag.affix }"
                        :data-path="tag.path"
                        :data-zone="tagZone(tag)"
                        :data-affix="tag.affix ? '1' : '0'"
                        @click="navigateTo(tag)"
                        @contextmenu="openContextMenu(tag, $event)"
                        @click.middle="closeTag(tag)"
                    >
                        <i v-if="tag.affix" class="iconfont icon-report-default tag-home" />
                        <span v-else-if="isFixed(tag)" class="tag-fixed-dot" />
                        <span class="tag-title">{{ t(tag.title) }}</span>
                    </div>
                </div>

                <!-- 未固定区：仅区内排序 -->
                <div v-show="normalZoneTags.length" :key="`normal-${listRenderKey}`" ref="normalListRef" class="tags-view-list__zone" data-zone="normal">
                    <div
                        v-for="tag in normalZoneTags"
                        :key="tag.path"
                        :ref="el => setTagRef(tag.path, el as HTMLElement | null)"
                        class="tags-view-item"
                        :class="{ active: isActive(tag) }"
                        :data-path="tag.path"
                        :data-zone="tagZone(tag)"
                        data-affix="0"
                        @click="navigateTo(tag)"
                        @contextmenu="openContextMenu(tag, $event)"
                        @click.middle="closeTag(tag)"
                    >
                        <span class="tag-title">{{ t(tag.title) }}</span>
                        <span class="tag-close-slot">
                            <i class="iconfont icon-cancel tag-close" @click="closeTag(tag, $event)" />
                        </span>
                    </div>
                </div>
            </div>
        </ScrollPane>

        <ul v-show="contextVisible" class="context-menu" :style="{ left: contextLeft + 'px', top: contextTop + 'px' }">
            <li @click="contextTag && refreshTag(contextTag)">
                <i class="iconfont icon-refresh" />
                {{ t("刷新页面") }}
            </li>
            <li v-if="contextTag && !contextTag.affix && !isFixed(contextTag)" @click="contextTag && closeTag(contextTag)">
                <i class="iconfont icon-close" />
                {{ t("关闭页面") }}
            </li>
            <li @click="closeOthers">
                <i class="iconfont icon-chevron-right" />
                {{ t("关闭其他") }}
            </li>
            <li v-if="contextTag && canPinTag(contextTag) && !isFixed(contextTag)" @click="contextTag && fixTag(contextTag)">
                <i class="iconfont icon-bookmark-filled" />
                {{ t("固定页面") }}
            </li>
            <li v-if="contextTag && !contextTag.affix && isFixed(contextTag)" @click="contextTag && unfixTag(contextTag)">
                <i class="iconfont icon-bookmark_stroke" />
                {{ t("取消固定") }}
            </li>
            <li @click="closeAll">
                <i class="iconfont icon-remove-circle" />
                {{ t("关闭所有") }}
            </li>
        </ul>
    </div>
</template>

<style lang="scss" scoped>
.tags-view-container {
    position: relative;
    flex: 1;
    height: var(--layout-tag-height);
    overflow: hidden;
}

.scroll-pane {
    height: 100%;
}

.tags-view-list {
    display: flex;
    align-items: center;
    height: 100%;
    min-width: min-content;
}

.tags-view-list__zone {
    display: flex;
    align-items: center;
    height: 100%;
    flex-shrink: 0;
}

.tags-view-item {
    position: relative;
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    height: var(--layout-tag-height);
    padding: 0 8px;
    font-size: var(--font-size-label);
    line-height: var(--line-height-label);
    color: var(--main-color-hover);
    background-color: transparent;
    border-radius: 8px 8px 0 0;
    cursor: pointer;
    user-select: none;
    // 仅文字色过渡；背景与凹角同步瞬时切换，避免「底色渐变 + 伪元素突现」造成卡顿感
    transition: color var(--transition-fast);

    // 原位置占位隐藏（helper 才是跟手预览）
    &--ghost {
        visibility: hidden;
        opacity: 0;
    }

    .tag-title {
        white-space: nowrap;
    }

    .tag-home {
        flex-shrink: 0;
        margin-right: 8px;
        font-size: 12px;
        line-height: 1;
        color: var(--main-color-active);
    }

    .tag-fixed-dot {
        flex-shrink: 0;
        width: 6px;
        height: 6px;
        margin-right: 8px;
        border-radius: 50%;
        background-color: var(--primary);
    }

    .tag-close-slot {
        flex-shrink: 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 12px;
        height: 12px;
        margin-left: 6px;
    }

    .tag-close {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 12px;
        height: 12px;
        font-size: 12px;
        line-height: 1;
        color: var(--main-color-active);
        border-radius: 50%;
        opacity: 0;
        pointer-events: none;
        transition: opacity var(--transition-fast);

        &:hover {
            background-color: var(--close-btn-hover-bg);
        }
    }

    &:hover:not(.active) {
        color: var(--button-primary-hover);

        .tag-close {
            opacity: 1;
            pointer-events: auto;
        }
    }

    // 凹角伪元素常驻，仅用透明度切换，避免每次激活重建 box-shadow/clip-path
    &::before,
    &::after {
        content: "";
        position: absolute;
        bottom: 0;
        width: 8px;
        height: 8px;
        border: none;
        box-shadow: 0 0 0 30px var(--main-focus-active);
        opacity: 0;
        pointer-events: none;
    }

    &::before {
        left: -8px;
        border-bottom-right-radius: 8px;
        clip-path: inset(0 -30px 0 0);
    }

    &::after {
        right: -8px;
        border-bottom-left-radius: 8px;
        clip-path: inset(0 0 0 -30px);
    }

    &.active {
        z-index: 1;
        color: var(--primary);
        background-color: var(--main-focus-active);

        &::before,
        &::after {
            opacity: 1;
        }

        .tag-home {
            color: var(--primary);
        }

        .tag-close {
            opacity: 1;
            pointer-events: auto;
        }
    }
}

// 右键菜单
.context-menu {
    position: fixed;
    z-index: 3000;
    min-width: 140px;
    margin: 0;
    padding: 5px 0;
    list-style: none;
    background-color: var(--main-background);
    border-radius: 4px;
    font-size: var(--font-size-label);
    line-height: var(--line-height-label);
    font-weight: var(--font-weight-regular);
    color: var(--main-color);
    box-shadow: 2px 2px 3px 0 rgba(0, 0, 0, 0.3);

    li {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 7px 16px;
        cursor: pointer;
        transition: background-color var(--transition-fast);

        .iconfont {
            font-size: 14px;
            line-height: 1;
        }

        &:hover {
            background-color: var(--main-background-active);
            color: var(--primary);
        }
    }
}
</style>

<!-- helper 挂 body；拖拽预览始终保持选中态样式，真实列表等松手后再改路由 -->
<style lang="scss">
.tags-view-item--fallback,
.tags-view-item--drag {
    z-index: 100000 !important;
    pointer-events: none !important;
    opacity: 1 !important;
    visibility: visible !important;
    cursor: pointer !important;
    color: var(--primary) !important;
    background-color: var(--main-focus-active) !important;
    box-sizing: border-box;
}
</style>
